import React, { memo } from 'react';
import { useIntl } from 'react-intl';

import { ActionsMenu } from '../../../components/ActionsMenu/ActionsMenu';
import { MenuActions } from '../../../types/MenuActions';
import { State } from '../types';

interface Props {
  enableManageColumns: boolean;
  enablePageSizeSelect: boolean;
  pageSize: number;
  pageSizes: number[];
  state: State;
  onAddRemoveColumns: () => void;
  onReorderColumns: () => void;
  onUpdatePageSize: (updatedPageSize: number) => void;
  onLoadView?: (state: State) => void;
  onSaveView?: (state: State) => void;
}

export const SettingsMenu = memo(
  ({
    enableManageColumns = true,
    enablePageSizeSelect = true,
    pageSize,
    pageSizes,
    state,
    onAddRemoveColumns,
    onLoadView,
    onReorderColumns,
    onSaveView,
    onUpdatePageSize,
  }: Props) => {
    const intl = useIntl();
    const viewActions = [];
    if (onSaveView) {
      viewActions.push({
        value: 'save-view',
        label: intl.formatMessage({
          id: 'SettingsMenu.saveView',
          defaultMessage: 'Save view',
          description: 'Label for the save view menu option',
        }),
        onClick: () => onSaveView(state),
      });
    }
    if (onLoadView) {
      viewActions.push({
        value: 'load-view',
        label: intl.formatMessage({
          id: 'SettingsMenu.loadView',
          defaultMessage: 'Load view',
          description: 'Label for the load view menu option',
        }),
        onClick: () => onLoadView(state),
      });
    }

    const groupedActions: MenuActions<string | number> = [
      ...(enableManageColumns
        ? [
            {
              label: '',
              options: [
                {
                  value: 'add-remove-columns',
                  label: intl.formatMessage({
                    id: 'SettingsMenu.addRemoveColumns',
                    defaultMessage: 'Add or delete columns',
                    description:
                      'Label for the add or delete columns menu option',
                  }),
                  onClick: onAddRemoveColumns,
                },

                {
                  value: 'reorder-columns',
                  label: intl.formatMessage({
                    id: 'SettingsMenu.reorderColumns',
                    defaultMessage: 'Reorder columns',
                    description: 'Label for the reorder columns menu option',
                  }),
                  onClick: onReorderColumns,
                },

                ...viewActions,
              ],
            },
          ]
        : []),
      ...(enablePageSizeSelect
        ? [
            {
              label: intl.formatMessage({
                id: 'SettingsMenu.pageSizeSelect',
                defaultMessage: 'Show up to',
                description: 'Label for the page size select menu option',
              }),
              options: pageSizes.map((count) => {
                const isActive = pageSize === count;
                return {
                  ariaLabel: `${count} rows per page`,
                  isActive,
                  value: count,
                  label: `${intl.formatMessage(
                    {
                      id: 'SettingsMenu.pageSizeOption',
                      defaultMessage: '{count} rows',
                      description:
                        'Option label for the table page size. Example: count = "25"',
                    },
                    { count },
                  )}`,
                  onClick: isActive ? undefined : () => onUpdatePageSize(count),
                };
              }),
            },
          ]
        : []),
    ];

    return (
      <ActionsMenu
        enablePortal
        icon="gear"
        actions={groupedActions}
        name={`table settings menu has ${pageSize} rows per page selected`}
        size="s"
        tooltip={intl.formatMessage({
          id: 'SettingsMenu.tooltip.tableSettings',
          defaultMessage: 'Table settings',
          description: 'Tooltip for the table settings menu icon',
        })}
      />
    );
  },
);
